import Footer from "@/components/Footer";
import Navigation from "@/components/Navigation";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { CheckCircle, BarChart3, Shield, Cpu, TrendingUp, Star } from "lucide-react";

export default function ERPPage() {
  useEffect(() => {
    const btns = Array.from(
      document.querySelectorAll<HTMLElement>('[role="tab"]')
    );
    const panels = Array.from(
      document.querySelectorAll<HTMLElement>('[role="tabpanel"]')
    );

    const activate = (id: string) => {
      btns.forEach((b) => {
        const on = b.dataset.tab === id;

        b.setAttribute("aria-selected", on ? "true" : "false");

        if (on) {
          // ACTIVE
          b.classList.remove(
            "border-slate-200",
            "text-slate-600",
            "bg-white",
            "hover:text-blue-600"
          );

          b.classList.add(
            "border-blue-600",
            "text-white",
            "bg-gradient-to-r",
            "from-blue-600",
            "to-indigo-600",
            "shadow-lg",
            "hover:text-white"
          );
        } else {
          // INACTIVE
          b.classList.remove(
            "border-blue-600",
            "text-white",
            "bg-gradient-to-r",
            "from-blue-600",
            "to-indigo-600",
            "shadow-lg",
            "hover:text-white"
          );

          b.classList.add(
            "border-slate-200",
            "text-slate-600",
            "bg-white",
            "hover:text-blue-600"
          );
        }
      });

      panels.forEach((p) => {
        p.classList.toggle("hidden", p.dataset.panel !== id);
      });
    };

    const onClick = (e: Event) => {
      const el = e.currentTarget as HTMLElement;
      const id = el.dataset.tab!;
      activate(id);
    };

    const onKey = (e: KeyboardEvent) => {
      const el = e.currentTarget as HTMLElement;
      const i = btns.indexOf(el);
      if (e.key === "ArrowRight") btns[(i + 1) % btns.length].focus();
      if (e.key === "ArrowLeft")
        btns[(i - 1 + btns.length) % btns.length].focus();
    };

    btns.forEach((b) => {
      b.addEventListener("click", onClick);
      b.addEventListener("keydown", onKey);
    });

    activate("acc");

    return () => {
      btns.forEach((b) => {
        b.removeEventListener("click", onClick);
        b.removeEventListener("keydown", onKey);
      });
    };
  }, []);

  const fade = {
    initial: { opacity: 0, y: 40 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: false, margin: "-80px" },
    transition: { duration: 0.7 },
  };

  return (
    <div
      className="min-h-screen"
      style={{ fontFamily: "'Roboto', sans-serif" }}
    >
      <Navigation />

      {/* HERO - Dark Blue Background */}
      <motion.section
        {...fade}
        className="relative min-h-[70vh] md:min-h-[90vh] flex items-center overflow-hidden bg-[#040814] text-white pt-20 pb-12"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-8 -left-8 w-[300px] h-[300px] md:w-[400px] md:h-[400px] bg-blue-500/10 blur-3xl rounded-full"></div>
          <div className="absolute bottom-0 -right-8 w-[350px] h-[350px] md:w-[450px] md:h-[450px] bg-indigo-500/10 blur-3xl rounded-full"></div>
        </div>

        <div className="relative container mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center px-6 md:px-0">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="space-y-6 pl-10"
          >
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight">
              Enterprise Resource Planning
            </h1>

            <p className="text-slate-300 leading-relaxed">
              Enterprise resource planning (ERP) is business process management
              software that allows an organization to integrate and automate
              core business processes across departments.
            </p>

            <p className="text-slate-300 leading-relaxed">
              ERP integrates product planning, manufacturing, sales, marketing,
              and more — into a single system with one database.
            </p>

            <p className="text-slate-300 leading-relaxed">
              While every company has different needs, ERP systems help connect
              processes, improve visibility, and streamline operations.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative flex justify-center"
          >

            <div className="relative max-w-xs sm:max-w-md rounded-2xl bg-gradient-to-br from-white to-white p-2 shadow-lg ring-1 ring-white/30 backdrop-blur-md">
              <img
                src="/erpprocess.png"
                alt="ERP process illustration"
                className="rounded-xl shadow-md w-full h-auto"
              />
            </div>
          </motion.div>
        </div>
      </motion.section> 

      {/* MODULES - Light Background */}
      <motion.section
        {...fade}
        className="py-16 md:py-24 bg-gradient-to-b from-white to-gray-50 text-slate-900"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10 md:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-6">
              Comprehensive ERP
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                Modules Suite
              </span>
            </h2>
            <p className="text-lg text-gray-600">
              All modules connect together to create one unified system.
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="flex justify-center mb-8 px-4 sm:px-0">
              <div
                role="tablist"
                aria-label="ERP Modules"
                className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-2 rounded-2xl bg-white p-2 shadow-lg border border-gray-100"
              >
                {[ 
                  { id: "acc", icon: BarChart3, label: "Finance" }, 
                  { id: "mfg", icon: Cpu, label: "Manufacturing" },
                  { id: "sales", icon: TrendingUp, label: "Sales" }, 
                  { id: "svc", icon: Shield, label: "Service" } 
                ].map((tab) => (
                  <button
                    key={tab.id}
                    className="tab-btn rounded-xl px-6 py-3 md:px-6 text-base font-semibold transition-all duration-300 border border-slate-200 text-slate-600 bg-white hover:border-blue-400 hover:text-blue-600 w-full sm:w-auto flex justify-start sm:justify-center"
                    role="tab"
                    aria-selected={tab.id === "acc"}
                    data-tab={tab.id}
                  >
                    <div className="flex items-center gap-2">
                      <tab.icon className="w-5 h-5" />
                      <span>{tab.label}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-white p-4">
                <img
                  src="/Dashboard-Fullfillment.png"
                  alt="ERP Dashboard Preview"
                  className="w-full h-auto rounded-xl"
                />
              </div>

              <div className="space-y-8">
                {/* Finance */}
                <div role="tabpanel" data-panel="acc" className="space-y-6">
                  <h3 className="text-2xl font-bold text-gray-900">
                    Accounting & Finance
                  </h3>
                  <p className="text-gray-600">
                    Real-time financial visibility, automation, & compliance.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[ 
                      "General Ledger", "Accounts Payable", "Accounts Receivable", "Budgeting", "Tax Management", 
                      "Cash Flow", "Assets", "Compliance" 
                    ].map((i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2 p-3 rounded-xl hover:bg-blue-50 text-gray-700"
                      >
                        <CheckCircle className="text-green-500 w-5" />
                        <span>{i}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Manufacturing */}
                <div role="tabpanel" data-panel="mfg" className="space-y-6 hidden">
                  <h3 className="text-2xl font-bold text-gray-900">
                    Manufacturing
                  </h3>
                  <p className="text-gray-600">
                    Plan, produce, monitor & track everything.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[ 
                      "Production Planning", "Quality Control", "Inventory", "MRP", "Cost Tracking", "Maintenance" 
                    ].map((i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2 p-3 rounded-xl hover:bg-emerald-50 text-gray-700"
                      >
                        <CheckCircle className="text-green-500 w-5" />
                        <span>{i}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Sales */}
                <div role="tabpanel" data-panel="sales" className="space-y-6 hidden">
                  <h3 className="text-2xl font-bold text-gray-900">
                    Sales Management
                  </h3>
                  <p className="text-gray-600">
                    CRM, tracking, analytics — everything connected.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[ 
                      "Orders", "Quotes", "Contracts", "Commissions", "Customers", "Analytics" 
                    ].map((i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2 p-3 rounded-xl hover:bg-amber-50 text-gray-700"
                      >
                        <CheckCircle className="text-green-500 w-5" />
                        <span>{i}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Service */}
                <div role="tabpanel" data-panel="svc" className="space-y-6 hidden">
                  <h3 className="text-2xl font-bold text-gray-900">
                    Service Management
                  </h3>
                  <p className="text-gray-600">
                    SLAs, tickets, warranties & field service.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[ 
                      "Case Management", "Field Service", "Contracts", "Knowledge Base", "Support", "Service Analytics" 
                    ].map((i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2 p-3 rounded-xl hover:bg-purple-50 text-gray-700"
                      >
                        <CheckCircle className="text-green-500 w-5" />
                        <span>{i}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* PERFORMANCE - Dark Background */}
      <motion.section
        {...fade}
        className="py-20 bg-gradient-to-br from-gray-900 via-blue-950 to-indigo-950 text-white"
      >
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold mb-6">
            How does ERP improve business performance?
          </h2>

          <p className="text-slate-300 mb-4">
            ERP is an automation software. It automates business by performing
            various tasks more efficiently with information that otherwise would
            not have been available.
          </p>

          <p className="text-slate-300 mb-4">
            ERP simplifies most of the functions for the users, ultimately
            improving the fundamental process which is order fulfillment — which
            covers all the processes of the business.
          </p>

          <p className="text-slate-300 mb-4">
            Running reports in real time, having accessible data ready for
            analysis — now essential. ERP is a necessity for survival, not just
            performance.
          </p>

          <p className="text-slate-300 mb-4">
            For small businesses, ERP helps leverage time and resources, cut
            costs and stay ahead of competition. For SMEs, SMBs, and large
            companies, ERP is essential.
          </p>

          <p className="text-slate-300">
            76% of IT leaders say ERP improves operational efficiency — followed
            by support for global business, growth, and reducing IT costs.
          </p>
        </div>
      </motion.section>

      {/* FEATURES - Light Background */}
      <motion.section
        {...fade}
        className="py-20 bg-gradient-to-br from-indigo-50 via-white to-cyan-50 text-gray-800"
      >
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="md:w-full">
              <div className="inline-flex items-center gap-3 mb-4">
                <div className="bg-gradient-to-r from-yellow-400 to-pink-500 text-white p-2 rounded-full shadow-lg">
                  <Star className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-3xl font-extrabold text-gray-900">Feature / Benefits of ERP</h2>
                  <p className="text-gray-600 mt-1">All the tools you need to run and grow your business — elegantly.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                {[
                  { title: "Leads / Contacts / Deals", desc: "Track pipeline with ease" },
                  { title: "Calendars & Scheduling", desc: "Plan with precision" },
                  { title: "Quotes & Invoices", desc: "Professional billing" },
                  { title: "Product Catalogue", desc: "Centralized product data" },
                  { title: "Import / Export", desc: "Bulk operations & migrations" },
                  { title: "Currencies & Taxes", desc: "Global-ready finance" }
                ].map((item) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35 }}
                    className="rounded-2xl p-6 bg-gradient-to-r from-blue-100 to-indigo-200 text-gray-800 shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300"
                  >
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md">
                        <CheckCircle className="w-6 h-6" />
                      </div>
                      <div>
                        <div className="font-semibold text-lg text-gray-900">{item.title}</div>
                        <div className="text-sm text-gray-600 mt-2">{item.desc}</div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      <Footer />
    </div>
  );
}